/**
 * Everything the review screen *says*, worked out without a browser
 * (plan 4 §5): which group a wall sits in, which state it shows, and the
 * sentences — the count line, the hints, the reason per wall.
 *
 * Two rules this module keeps:
 *
 * - **Groups are fixed by the reading, states by the latest check.** A wall
 *   that arrived as „braucht dich“ stays in that group when a dry run checks
 *   it or a person sets it — it collapses in place. Moving rows between
 *   groups while somebody types would take the row away from under the
 *   cursor.
 * - **A person's value wins over any answer.** Whatever a dry run says about
 *   a resolved wall, the screen shows „von dir“ (A-014: the machine checks,
 *   only a person confirms). The server says the same in `resolvedMm` and
 *   keeps its own word in `status`; the screen takes the person's list it
 *   holds itself, so a figure shows the moment it is set, not a round trip
 *   later, and a figure taken back is gone at once.
 * - **„geprüft“ only where a check could have said no.** The chain marks a
 *   wall `geprueft` once its three readings agree and nothing contradicts
 *   them. Where the ring was checked (E-15, per axis), the sum of its axis
 *   could have contradicted it. Where it was not — an open chain, a wall
 *   without a figure — nothing could: S1011-08 reads the customer's „17,5“
 *   (meant 1,75) three times alike, and it arrives `geprueft`. Those walls
 *   say „gelesen“, quietly, so nobody reads more into them than is there
 *   (Messlauf 11.10.2026). A figure a person adds can make the ring
 *   checkable; then the walls turn „geprüft“ in place.
 */
import type { ReadingReviewOut, Resolution, ReviewWall, RingFix, RingHint } from "@/api";
import { formatMetres, formatMm } from "@/lib/format";

export type RowGroup = "needs" | "checked" | "unmeasured";
export type RowState = "geprueft" | "gelesen" | "offen" | "uneinig" | "kein_mass" | "von_dir";

export interface ReviewRow {
  wall: ReviewWall;
  group: RowGroup;
  state: RowState;
  /** The figure the wall carries now — read (and checked), or a person's. */
  valueMm: number | null;
  resolution: Resolution | null;
  /** On a read wall a person changed: what was read there. */
  displacedMm: number | null;
  /** …and whether the ring had checked it, when it arrived. */
  displacedChecked: boolean;
}

const GROUP_OF: Record<ReviewWall["status"], RowGroup> = {
  offen: "needs",
  mehrdeutig: "needs",
  geprueft: "checked",
  unbemasst: "unmeasured",
};

const STATE_OF: Record<ReviewWall["status"], RowState> = {
  geprueft: "geprueft",
  offen: "offen",
  mehrdeutig: "uneinig",
  unbemasst: "kein_mass",
};

export function rowsOf(initial: ReadingReviewOut, current: ReadingReviewOut, resolutions: Resolution[]): ReviewRow[] {
  const mine = new Map(resolutions.map((resolution) => [resolution.wallId, resolution]));
  const now = new Map(current.walls.map((wall) => [wall.wallId, wall]));
  // The latest check decides: a ring checked now checks the walls of every
  // axis that closes — the walls of an axis that misses are `mehrdeutig`.
  const ringChecked = current.ring.checked;
  return initial.walls.map((arrived) => {
    const wall = now.get(arrived.wallId) ?? arrived;
    const resolution = mine.get(arrived.wallId) ?? null;
    if (resolution !== null) {
      return {
        wall,
        group: GROUP_OF[arrived.status],
        state: "von_dir",
        valueMm: resolution.valueMm,
        resolution,
        displacedMm: arrived.status === "geprueft" ? arrived.valueMm : null,
        displacedChecked: initial.ring.checked,
      };
    }
    return {
      wall,
      group: GROUP_OF[arrived.status],
      state: wall.status === "geprueft" && !ringChecked ? "gelesen" : STATE_OF[wall.status],
      valueMm: wall.status === "geprueft" ? wall.valueMm : null,
      resolution: null,
      displacedMm: null,
      displacedChecked: false,
    };
  });
}

/** Read by the machine and taken as it is — checked or not, nothing for a person to do. */
export function settled(row: ReviewRow): boolean {
  return row.state === "geprueft" || row.state === "gelesen" || row.state === "von_dir";
}

/** The word a state carries beside its line and its plaque — never colour alone. */
export function stateWord(state: RowState): string {
  switch (state) {
    case "geprueft":
      return "geprüft";
    case "gelesen":
      return "gelesen";
    case "offen":
      return "offen";
    case "uneinig":
      return "uneinig";
    case "kein_mass":
      return "kein Maß";
    case "von_dir":
      return "von dir";
  }
}

export function needsYou(row: ReviewRow): boolean {
  return row.state === "offen" || row.state === "uneinig";
}

// ── Sentences ───────────────────────────────────────────────────────────────

const NUMBER_WORDS = ["keine", "eine", "zwei", "drei", "vier", "fünf", "sechs"];

function capitalised(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** 17,4 → „17“, 2,5 → „2,5“, 3,03 → „3“: whole above ten, to the half below. */
function times(factor: number): string {
  const value = factor >= 10 ? Math.round(factor) : Math.round(factor * 2) / 2;
  return String(value).replace(".", ",");
}

const TWO_SCALES = "Zwei Maßstäbe auf dem Blatt — Abgleich mit der Zeichnung nicht möglich.";

/** Whether a wall runs across the sheet or up it — from where the readings put it, geometry only. */
export function wallAxis(wall: Pick<ReviewWall, "start" | "end">): "h" | "v" {
  return Math.abs(wall.end[0] - wall.start[0]) >= Math.abs(wall.end[1] - wall.start[1]) ? "h" : "v";
}

const WHOLE = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 });

/**
 * `axis_unit` (§10.1): one axis closes in itself, but at 10, 100 or 1.000
 * times the other's scale — depths in cm read as mm, or the like. The
 * sentence names the comparison and the factor, and what to do; which way
 * round is the sheet's to say, so the factor is never below one.
 */
export const AXIS_UNIT_TEXT =
  "Wohl in einer anderen Einheit bemaßt als die {other} Maße (Faktor {factor}) — prüf die Einheit.";

/**
 * `reasonCode` + `reasonParams` → one sentence (plan 4 §5). The code is the
 * contract, and the code itself is never shown.
 */
export function reasonSentence(wall: ReviewWall): string {
  const { n, factor } = wall.reasonParams;
  switch (wall.reasonCode) {
    case "axis_unit": {
      const other = wallAxis(wall) === "h" ? "senkrechten" : "waagerechten";
      if (factor == null || factor <= 0) return `Wohl in einer anderen Einheit bemaßt als die ${other} Maße — prüf die Einheit.`;
      return fill(AXIS_UNIT_TEXT, { other, factor: WHOLE.format(factor >= 1 ? factor : 1 / factor) });
    }
    case "agreed":
      return "In allen drei Lesungen gleich.";
    case "flagged_unclear":
      return `Zahl schwer lesbar (${n ?? "eine"} von 3 Lesungen unsicher).`;
    case "unit_unknown":
      return "Einheit unklar — m, cm oder mm?";
    case "runs_disagree":
      return "Lesungen uneinig — wähl den richtigen Wert.";
    case "two_disagree":
      // The ratio check's finding, not the runs': they agreed. Two measures
      // do not fit the drawing together, and one of them is wrong.
      return "Passt im Verhältnis zur Zeichnung nicht zu einem anderen Maß — eins davon stimmt wohl nicht.";
    case "ratio":
      if (factor == null || factor <= 0) return "Passt nicht zur Zeichnung.";
      return factor >= 1
        ? `Passt nicht zur Zeichnung: ${times(factor)}-mal so lang wie gezeichnet.`
        : `Passt nicht zur Zeichnung: gezeichnet ${times(1 / factor)}-mal so lang.`;
    case "two_scales":
      return TWO_SCALES;
    case "ring_open":
      return "Umlauf geht nicht auf.";
    case "chain_mismatch":
      return "Maßkette geht nicht auf.";
    case "chain_gap":
      return "Maßkette hat eine Lücke.";
    case "unreadable":
      return "Zahl nicht lesbar.";
    case "implausible":
      return "Wert unplausibel für eine Wand.";
    case "none_read":
      return "Kein Maß auf dem Blatt.";
    case "too_few_valid":
      return `Nur ${n ?? 1} von 3 Lesungen verwertbar.`;
    default:
      // A code this table does not know yet. Its name is not for a person,
      // and the contract carries no sentence per wall — so the general one.
      return "Lesung unsicher — prüf den Wert.";
  }
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

/**
 * What the head says (plan 4 §5, Kopf; Kauf-Test and Messlauf 11.10.2026).
 * Data, not code, and **every figure through a placeholder** — the start
 * page's rule (`features/start/greetings.ts`): a sentence that carries its
 * count in the text is a sentence that one day claims the wrong one. `fill`
 * puts them in.
 *
 * Two things are said, not blurred:
 *
 * - **Who set a figure.** „gelesen“ where the machine read it, „von dir“
 *   where a person set it — „Alle Maße gesetzt“ read as if the person had
 *   set the machine's figures too.
 * - **What was checked.** „geprüft — der Umlauf geht auf“ only where the
 *   ring was checked and closes (E-15). Not to scale or not does not change
 *   that: a closed ring is the stronger check. Where no ring could be
 *   checked the figures are „übereinstimmend gelesen“, with the reason why
 *   not more — and the second line asks to read them against the sheet.
 */
export const HEAD_TEXTS = {
  nothingRead: "Kein Maß auf dem Blatt gelesen — tipp die Maße ein oder bearbeite den Umriss im Editor.",
  open: "{n} von {measured} {measures} {how} — {open} {need} dich.",
  openWithMine: "{n} von {measured} {measures} {how} · {mine} von dir gesetzt — {open} {need} dich.",
  /** How the machine's figures stand: checked by the ring, or only read alike. */
  how: { checked: "geprüft", read: "übereinstimmend gelesen" },
  all: { one: "Das eine Maß", many: "Alle {n} Maße" },
  allChecked: "{all} gelesen und geprüft — der Umlauf geht auf.",
  allReadOpenChain: "{all} übereinstimmend gelesen — die Zeichnung ist offen, es gibt keinen Umlauf zu prüfen.",
  allReadGaps: "{all} übereinstimmend gelesen — nicht jede Wand hat ein Maß, der Umlauf lässt sich nicht prüfen.",
  allStand: "Alle Maße stehen — {mine} von dir, {n} {how}.",
  allMine: "Alle Maße stehen — {mine} von dir.",
  /** The second line, once nothing is open: everything checked or set … */
  lookOver: "Sieh kurz drüber und übernimm.",
  /** … or figures among them no check could contradict … */
  readAgainstSheet: "Sieh die Maße mit dem Blatt durch.",
  /** … or a person's own figures that the ring says do not fit. */
  mineMiss: "Mit deinen Werten geht der Umlauf nicht auf — prüf sie.",
} as const;

/** `{name}` → its value; a placeholder without one stays visible, so a gap is seen, not hidden. */
export function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => (name in values ? String(values[name]) : whole));
}

/**
 * The header's count line (plan 4 §5, Kopf). Counts are words' numbers, not
 * measures. `current` is the latest check — its ring says whether anything
 * was checked, its form whether there is a ring at all.
 */
export function countLine(rows: ReviewRow[], current: Pick<ReadingReviewOut, "ring" | "candidates">): string {
  const measured = rows.filter((row) => row.group !== "unmeasured").length;
  const checked = rows.filter((row) => row.state === "geprueft").length;
  const read = rows.filter((row) => row.state === "gelesen").length;
  const mine = rows.filter((row) => row.state === "von_dir").length;
  const open = rows.filter(needsYou).length;
  // A ring that is checked checks every machine figure that is still
  // standing; one that is not checks none — never some of each.
  const ringChecked = current.ring.checked;
  const machine = ringChecked ? checked : read;
  const how = ringChecked ? HEAD_TEXTS.how.checked : HEAD_TEXTS.how.read;

  if (measured === 0 && mine === 0) return HEAD_TEXTS.nothingRead;
  if (open === 0) {
    if (mine > 0) return machine > 0 ? fill(HEAD_TEXTS.allStand, { mine, n: machine, how }) : fill(HEAD_TEXTS.allMine, { mine });
    const all = machine === 1 ? HEAD_TEXTS.all.one : fill(HEAD_TEXTS.all.many, { n: machine });
    if (ringChecked && current.ring.closed) return fill(HEAD_TEXTS.allChecked, { all });
    const closedForm = current.candidates[0]?.closed ?? false;
    return fill(closedForm ? HEAD_TEXTS.allReadGaps : HEAD_TEXTS.allReadOpenChain, { all });
  }
  return fill(mine > 0 ? HEAD_TEXTS.openWithMine : HEAD_TEXTS.open, {
    n: machine,
    measured,
    measures: plural(measured, "Maß", "Maßen"),
    how,
    mine,
    open,
    need: plural(open, "braucht", "brauchen"),
  });
}

/**
 * The head's second line, once nothing needs a person: the one thing left
 * to do — a last look; or, where figures stand that no check could
 * contradict, reading them against the sheet; or, where the person's own
 * figures leave the ring open (every wall of the axis that misses is
 * theirs, so none is „braucht dich“), checking those.
 */
export function nextLine(rows: ReviewRow[], ring: ReadingReviewOut["ring"]): string | null {
  if (!rows.some(settled) || rows.some(needsYou)) return null;
  if (ring.checked && !ring.closed) return HEAD_TEXTS.mineMiss;
  return rows.some((row) => row.state === "gelesen") ? HEAD_TEXTS.readAgainstSheet : HEAD_TEXTS.lookOver;
}

/** The quiet line under the count (plan 4 §5, Hinweise). */
export function hintLines(review: ReadingReviewOut, rows: ReviewRow[]): string[] {
  const lines: string[] = [];
  const open = rows.filter(needsYou).length;
  const withoutValue = rows.filter((row) => row.valueMm === null).length;
  const blank = rows.filter((row) => row.state === "kein_mass").length;
  if (open > 0 && withoutValue > 0) {
    lines.push(
      `${withoutValue} ${plural(withoutValue, "Wand", "Wände")} noch ohne Maß — die Stückliste rechnet ${plural(withoutValue, "sie", "sie")} nicht.`,
    );
  } else if (blank > 0) {
    lines.push(
      `${blank} ${plural(blank, "Wand", "Wände")} ohne Maß auf dem Blatt — ${plural(blank, "sie bleibt", "sie bleiben")} grau.`,
    );
  }
  if (review.ratioReasonCode === "two_scales") lines.push(TWO_SCALES);
  if (review.pageCount > 1) lines.push(`Seite 1 von ${review.pageCount} gelesen.`);
  return lines;
}

export const RING_CLOSES = "Mit deinem Wert schließt der Umlauf.";

/**
 * Above the list: why the ring is in doubt — whether it arrived that way or a
 * person's figure opened it — or, once a figure closes a ring that was not
 * closed before, that it does now. `null` where nothing is to be said: a
 * ring that closed by itself, and an open chain, which is never checked.
 * The sums are signed on the wire; a person is told how far, not which way.
 */
export function ringLine(initial: ReadingReviewOut["ring"], current: ReadingReviewOut["ring"]): string | null {
  if (current.checked && !current.closed) {
    const parts = [
      current.dxMm ? `waagerecht ${formatMm(Math.abs(current.dxMm))}` : null,
      current.dyMm ? `senkrecht ${formatMm(Math.abs(current.dyMm))}` : null,
    ].filter(Boolean);
    const rest = parts.length > 0 ? ` (${parts.join(", ")} Rest)` : "";
    return `Die Maße ergeben keinen geschlossenen Umlauf${rest}. Mindestens eins stimmt nicht.`;
  }
  if (current.checked && current.closed && !(initial.checked && initial.closed)) return RING_CLOSES;
  return null;
}

// ── The ring, where one axis misses (plan 4 §10.1) ─────────────────────────

/**
 * A sentence with its figures marked: the screen sets them in the mono face
 * (CI §4), the test reads the sentence as one string. `fill`'s rule —
 * every figure through a placeholder — with the placeholders named in
 * `figures` kept apart.
 */
export type SentencePart = string | { figure: string };

export function fillParts(
  template: string,
  values: Record<string, string | number>,
  figures: readonly string[] = [],
): SentencePart[] {
  const parts: SentencePart[] = [];
  let last = 0;
  for (const match of template.matchAll(/\{(\w+)\}/g)) {
    const [whole, name] = match as unknown as [string, string];
    const at = match.index ?? 0;
    if (at > last) parts.push(template.slice(last, at));
    const value = name in values ? String(values[name]) : whole;
    parts.push(figures.includes(name) && name in values ? { figure: value } : value);
    last = at + whole.length;
  }
  if (last < template.length) parts.push(template.slice(last));
  return parts;
}

export function partsText(parts: readonly SentencePart[]): string {
  return parts.map((part) => (typeof part === "string" ? part : part.figure)).join("");
}

export const RING_TEXTS = {
  axis: { h: "Waagerecht", v: "Senkrecht" },
  /** Information, no value (E-16): which axis, how far, which walls — and where to look. */
  hint: "{axis} {rest} Rest — einer von {walls} stimmt nicht. Schau aufs Blatt.",
  /** The suggestion in one sentence: what the sheet shows, what closes the ring, and why that is plausible. */
  fix: "{wall}: auf dem Blatt „{text}“. Mit {value} geht der Umlauf auf — das Komma ist wohl um {places} verrutscht.",
  fixPlaces: { one: "eine Stelle", many: "{n} Stellen" },
  /** Where the read figure does not say how far the comma moved. */
  fixPlain: "{wall}: auf dem Blatt „{text}“. Mit {value} geht der Umlauf auf — wohl ein verrutschtes Komma.",
  fixNoText: "{wall}: mit {value} geht der Umlauf auf — wohl ein verrutschtes Komma.",
  /** For a screen reader, once taken: the wall is the person's, never „geprüft“. */
  taken: "{wall} gesetzt: {value}, aus dem Vorschlag.",
} as const;

/**
 * What the screen says about the ring, above the list: the hint where one
 * axis misses (§10.1), else `ringLine`. One function, so the live region
 * reads out what the screen shows.
 */
export function ringSentence(initial: ReadingReviewOut, current: ReadingReviewOut): string | null {
  const hint = current.ringHint ?? null;
  return hint !== null ? partsText(ringHintParts(hint, initial.walls)) : ringLine(initial.ring, current.ring);
}

/** „Waagerecht 15.750 mm Rest — einer von W1, W3, W5, W7 stimmt nicht. Schau aufs Blatt.“ */
export function ringHintParts(hint: RingHint, walls: readonly Pick<ReviewWall, "wallId" | "label">[]): SentencePart[] {
  const labelOf = new Map(walls.map((wall) => [wall.wallId, wall.label]));
  const labels = hint.wallIds.map((id) => labelOf.get(id) ?? id.toUpperCase());
  // Each wall its own figure, the commas between them in the text face —
  // a list set wholly in mono reads as gaps, not as a list.
  return fillParts(
    RING_TEXTS.hint,
    { axis: RING_TEXTS.axis[hint.axis], rest: formatMm(Math.abs(hint.restMm)), walls: labels.join(", ") },
    ["rest", "walls"],
  ).flatMap((part) =>
    typeof part !== "string" && part.figure === labels.join(", ")
      ? labels.flatMap((label, index): SentencePart[] => (index === 0 ? [{ figure: label }] : [", ", { figure: label }]))
      : [part],
  );
}

/** How far the comma moved between what the readings read and the suggestion — `null` where that is not a clean shift. */
function commaPlaces(readMm: number | null, valueMm: number): number | null {
  if (readMm === null || readMm <= 0 || valueMm <= 0) return null;
  const places = Math.log10(readMm / valueMm);
  const whole = Math.round(places);
  return whole !== 0 && Math.abs(places - whole) < 0.01 ? Math.abs(whole) : null;
}

/**
 * „W3: auf dem Blatt „17,5“. Mit 1,75 m geht der Umlauf auf — das Komma ist
 * wohl um eine Stelle verrutscht.“ — the sheet's figure, the value that
 * closes the ring, and the reason in a few words. The value in metres, as
 * the sheet writes it.
 */
export function ringFixParts(fix: RingFix, wall: Pick<ReviewWall, "label" | "agreedMm" | "candidates">): SentencePart[] {
  const read = wall.agreedMm ?? (wall.candidates.length === 1 ? wall.candidates[0]!.valueMm : null);
  const places = commaPlaces(read, fix.valueMm);
  const values = { wall: wall.label, text: fix.text ?? "", value: formatMetres(fix.valueMm) };
  const figures = ["wall", "text", "value"];
  if (fix.text === null || fix.text === "") return fillParts(RING_TEXTS.fixNoText, values, figures);
  if (places === null) return fillParts(RING_TEXTS.fixPlain, values, figures);
  const placesText = places === 1 ? RING_TEXTS.fixPlaces.one : fill(RING_TEXTS.fixPlaces.many, { n: NUMBER_WORDS[places] ?? places });
  return fillParts(RING_TEXTS.fix, { ...values, places: placesText }, figures);
}

// ── The form ─────────────────────────────────────────────────────────────

/**
 * The subline of „Umriss nicht sicher erkannt.“ (plan 4 §9): how many
 * readings saw how many shapes, and what happens after a choice — the
 * measures of the chosen one are read. Without a shape: draw it.
 */
export function formLine(candidates: ReadingReviewOut["candidates"]): string {
  const readings = candidates.reduce((sum, candidate) => sum + candidate.votes, 0) || 3;
  const readingsWord = capitalised(NUMBER_WORDS[readings] ?? String(readings));
  const lead = `${readingsWord} ${plural(readings, "Lesung", "Lesungen")}`;
  if (candidates.length === 0) return `${lead}, keine stimmige Form. Zeichne den Umriss selbst.`;
  const shapes = NUMBER_WORDS[candidates.length] ?? String(candidates.length);
  return `${lead}, ${shapes} ${plural(candidates.length, "Form", "Formen")}. Wähl die, die zu deinem Blatt passt — dann werden ihre Maße gelesen.`;
}

/**
 * Why nothing is chosen for the person (§10.1): in the case that started it
 * the follow-up question voted twice for the minority. The votes stand at
 * every reading — they are how often the machine saw it, not which is right.
 */
export const VOTES_NOTE = "Die Zahl der Lesungen ist kein Urteil — vergleich mit dem Blatt.";

/** „2 von 3 Lesungen · geschlossen“ under a form candidate. */
export function candidateLine(candidate: ReadingReviewOut["candidates"][number], total: number): string {
  return `${candidate.votes} von ${total} ${plural(total, "Lesung", "Lesungen")} · ${candidate.closed ? "geschlossen" : "offen"}`;
}

/** „Lesart A“ — by the label the reading carries, stable across a choice (§9.1). */
export function candidateName(candidate: Pick<ReadingReviewOut["candidates"][number], "label">): string {
  return `Lesart ${candidate.label}`;
}

/**
 * „Umriss von dir gewählt (Lesart B)“ — the head of a review whose form a
 * person chose (§10.1): a choice, not the machine's check. The chosen
 * reading is listed first.
 */
export function chosenLine(review: Pick<ReadingReviewOut, "formStatus" | "candidates">): string | null {
  if (review.formStatus !== "gewaehlt") return null;
  const chosen = review.candidates[0];
  return chosen ? `Umriss von dir gewählt (${candidateName(chosen)})` : "Umriss von dir gewählt";
}
